// sidebarFavorites — 사이드바 메뉴 즐겨찾기(사용자별) 캐시·계산·저장 진행 상태.
// 정본은 서버(user_menu_favorites · GET /api/sidebar-menu/favorites)다. 브라우저 캐시는 첫 화면용이고
// 캐시의 uid 가 지금 사용자와 같을 때만 쓴다. 메뉴 key 문자열은 여기 두지 않는다(정본은 sidebarMenu.js DEFAULT_NAV).

export const FAVORITES_CACHE_KEY = 'gwSidebarFavorites';
// 같은 창의 다른 사이드바가 캐시를 바꿨다는 신호 — AppSidebar 가 듣고 캐시를 다시 읽는다.
export const FAVORITES_EVENT = 'gw-sidebar-favorites';
// 서버 routers/sidebar_menu.py MAX_FAVORITES 와 같은 값.
export const MAX_FAVORITES = 30;

const sameUid = (a, b) => String(a) === String(b);

/**
 * 캐시된 즐겨찾기 배열. 없음·깨짐이면 [] · 🔴 캐시가 다른 사용자 것이거나 uid 가 비면 null(받는 쪽이 무시한다).
 * keys 가 배열이 아니면 [] — 참/거짓으로 검사하지 않는다(가로챈 응답이 함수·객체를 넣을 수 있다).
 */
export function readCachedFavorites(uid) {
  if (!uid) return null;
  let cached;
  try {
    const raw = localStorage.getItem(FAVORITES_CACHE_KEY);
    if (!raw) return [];
    cached = JSON.parse(raw);
  } catch (e) {
    return [];
  }
  if (!cached || typeof cached !== 'object' || Array.isArray(cached)) return [];
  if (!sameUid(cached.uid, uid)) return null;
  if (!Array.isArray(cached.keys)) return [];
  return cached.keys.filter((k) => typeof k === 'string');
}

/**
 * 캐시에 쓰고 같은 창의 사이드바에 알린다. 반환값 = 쓰기 성공 여부.
 * 🔴 쓰기가 성공했을 때만 이벤트를 쏜다(sidebarMenu.writeCachedConfig 와 다르다) — 실패한 쓰기 뒤 이벤트를 받은
 *    사이드바가 옛 캐시로 되돌아가면 다음 토글에서 방금 추가한 것을 잃는다.
 */
export function writeCachedFavorites(uid, keys) {
  if (!uid) return false;
  try {
    localStorage.setItem(FAVORITES_CACHE_KEY, JSON.stringify({ uid, keys }));
  } catch (e) {
    return false;   // 저장 공간 부족·사생활 모드 — 캐시 없이 동작
  }
  try { window.dispatchEvent(new Event(FAVORITES_EVENT)); } catch (e) { /* noop */ }
  return true;
}

// 있으면 빼고 없으면 끝에 붙인 새 배열(원본 불변).
export function toggleFavorite(keys, key) {
  const list = Array.isArray(keys) ? keys : [];
  return list.includes(key) ? list.filter((k) => k !== key) : list.concat([key]);
}

/**
 * «내 즐겨찾기» 묶음에 그릴 메뉴 — keys 순서대로, navItems 에 있고(섹션·구분선 제외) canSee 가 참인 것.
 * 권한이 빠진 key 는 숨기기만 한다(지우지 않는다). 같은 key 가 두 번 있으면 처음 것만.
 */
export function favoriteItems(navItems, keys, canSee) {
  if (!Array.isArray(navItems) || !Array.isArray(keys)) return [];
  const seen = new Set();
  const out = [];
  keys.forEach((key) => {
    if (seen.has(key)) return;
    seen.add(key);
    const it = navItems.find((x) => x && !x.section && !x.divider && x.key === key);
    if (!it || (typeof canSee === 'function' && !canSee(it))) return;
    out.push(it);
  });
  return out;
}

// ── 저장 진행 상태 — 사이드바 여러 벌이 공유한다(모듈 한 벌 · 숨은 임베드 사이드바 포함) ──
// favPending = 나가 있는 토글 수 · favSeq = 로컬 변경 횟수(요청을 보낸 뒤 바뀌었으면 그 응답을 버린다).
let favPending = 0;
let favSeq = 0;

export function beginFavoriteSave() {
  favPending += 1;
  favSeq += 1;
}

// 토글 하나가 끝났다. 🔴 이미 0 이면 아무것도 안 하고 거짓 · 나가 있는 토글이 0 이 됐으면 참.
// seq 를 올려 토글이 나가 있는 동안 출발한 처음 불러오기 응답이 늦게 와도 버리게 한다.
export function endFavoriteSave() {
  if (favPending <= 0) return false;
  favPending -= 1;
  favSeq += 1;
  return favPending === 0;
}

export function favoriteSeq() {
  return favSeq;
}

export function favoritesBusy() {
  return favPending > 0;
}

// 이벤트로 남의 값을 받았을 때 — 그 전에 출발한 응답을 버리게 한다.
export function noteFavoriteChange() {
  favSeq += 1;
}

// 시험용 — 앞 시험이 남긴 진행 상태를 비운다.
export function resetFavoriteSync() {
  favPending = 0;
  favSeq += 1;
}
